import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { isEmpty } from 'lodash';

import WithConfirm from '~/components/ConfirmModal';
import Button from '~/components/core/Atomic/Buttons/Button';
import CancelButton from '~/components/core/Buttons/CancelButton';
import {
  useFormikValidationErrorToast,
  ValidationErrorToastContainer,
} from '~/components/hooks/useFormikValidationErrorToast';
import LoadingDialog from '~/components/LoadingDialog';

import copies from './copies.json';
import { resolveFnolItemPath } from './fnolErrorItemPath';

const FnolConfigurationFooter = ({ onCancel, onSaveDraft, showSaveDraft = false }) => {
  const { handleSubmit, isSubmitting, touched, values } = useFormikContext();
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  // Configured fields are stored flat under incident_details whatever section they appear in, so the
  // default path-derived label names the storage location rather than the section the admin edited.
  useFormikValidationErrorToast({
    toastId: 'fnol-configuration-form-errors',
    resolveItemPath: resolveFnolItemPath(values),
  });

  const isBusy = isSubmitting || isSavingDraft;

  const handleSaveDraftClick = async () => {
    setIsSavingDraft(true);
    try {
      await onSaveDraft(values);
    } finally {
      setIsSavingDraft(false);
    }
  };

  return (
    <>
      <ValidationErrorToastContainer containerId="fnol-configuration-form-errors" />
      <div className="fixed bottom-0 left-0 h-80 w-full bg-white shadow-xl">
        <div className="mx-20 flex h-full items-stretch justify-center">
          <div>
            <WithConfirm
              title={copies.exit_confirm.title}
              contentText={copies.exit_confirm.text}
              primaryButtonName="YES, LEAVE"
              shouldCloseOnPrimary
              centerDialog
              disableConfirm={isEmpty(touched)}
            >
              <CancelButton
                onClick={onCancel}
                disabled={isBusy}
                classNameOverride="my-20"
                withMarginRight={!showSaveDraft}
              />
            </WithConfirm>
            {showSaveDraft && (
              <Button
                disabled={isBusy}
                onClick={handleSaveDraftClick}
                variant="outlined"
                className="mx-12 my-20 border border-blue-600 text-blue-600"
              >
                SAVE AS DRAFT
              </Button>
            )}
            <Button disabled={isBusy} onClick={handleSubmit} variant="contained" color="primary" className="my-20">
              SAVE
            </Button>
          </div>
        </div>
      </div>
      {isSubmitting && <LoadingDialog text="Saving FNOL Config..." isError={false} track="Save FNOL Config" />}
      {isSavingDraft && <LoadingDialog text="Saving Draft..." isError={false} track="Save FNOL Config Draft" />}
    </>
  );
};

FnolConfigurationFooter.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onSaveDraft: PropTypes.func.isRequired,
  showSaveDraft: PropTypes.bool,
};

export default FnolConfigurationFooter;
